'use strict';

define('vbc/private/utils',[
  'vbc/private/constants',
], (Constants) => {
  class Utils {
    /**
     * Generate a string unique Id.
     * @return {string} a unique id
     */
    static generateUniqueId() {
      return Math.random().toString(36).substr(2, 9);
    }

    /**
     * A very simplistic check to determine whether code is running on SW vs. UI thread
     *
     * @returns {boolean} true, if the code is executing on the service worker thread in the absence of a global
     * window object.
     */
    static isWorkerThread() {
      return globalThis.window === undefined;
    }

    /**
     * Returns true if the value is an object. An array is not counted as an object for this
     * evaluation.
     *
     * @param value The object to test
     * @returns {boolean} True if the object is non-null, defined, and an object
     */
    static isObject(value) {
      return value && typeof value === 'object' && !Array.isArray(value);
    }

    /**
     * Return true if object is thenable
     * Because of zonejs, instanceof Promise does not work to identify a Promise
     */
    static isThenable(obj) {
      return obj !== null && typeof obj === 'object' && typeof obj.then === 'function';
    }

    /**
     * Return a promise to load a resource.
     * Reject with the error if there was an error or the file doesn't exist
     *
     * @param  {String} resource the path to the resource to load
     * @returns {Promise} a promise resolving to the content of the resource
     */
    static getResource(resource) {
      return new Promise((resolve, reject) => {
        requirejs([resource],
          (loaded) => {
            resolve(loaded);
          },
          (reason) => {
            reject(reason);
          });
      });
    }

    /**
     * Return true if the application is a host application (aka unified application, v2)
     * @return {boolean}
     */
    static isHostApplication(vbInitConfig = globalThis.vbInitConfig) {
      return vbInitConfig && vbInitConfig.APP_TYPE === 'unified';
    }

    /**
     * Return true if the application is running in unit test
     * @return {boolean}
     */
    static isUnitTestMode(vbInitConfig = globalThis.vbInitConfig) {
      return vbInitConfig && vbInitConfig.TEST_MODE === Constants.TestMode.UNIT_TEST;
    }

    /**
     * Return true if the application is running in action chain test
     * @return {boolean}
     */
    static isActionChainTestMode(vbInitConfig = globalThis.vbInitConfig) {
      return vbInitConfig && vbInitConfig.TEST_MODE === Constants.TestMode.ACTION_CHAIN;
    }

    /**
     * Return true if the application is running in puppeteer test
     * @return {boolean}
     */
    static isPuppeteerTestMode(vbInitConfig = globalThis.vbInitConfig) {
      return vbInitConfig && vbInitConfig.TEST_MODE === Constants.TestMode.PUPPETEER;
    }

    /**
     * Implements optional chaining.
     * Example:
     * <pre>
     *  const adventurer = {
     *    name: 'Alice',
     *    cat: {
     *      name: 'Dinah'
     *    }
     *  };
     *  const dogName = adventurer.dog?.name;
     *  const alsoDogName = Utils.safelyGet(adventurer, 'dog', 'name');
     *
     * </pre>
     * @param {Object} target
     * @param {string[]} properties
     * @returns {*}
     */
    static safelyGet(target, ...properties) {
      return properties.reduce(
        (currentTarget, propertyName) => {
          if (currentTarget !== null && typeof currentTarget === 'object') {
            return currentTarget[propertyName];
          }

          return undefined;
        },
        target,
      );
    }

    /**
     * Implements optional chaining when last property is a function.
     *
     * Example:
     * <pre>
     *  const adventurer = {
     *    name: 'Alice',
     *    cat: {
     *      name: 'Dinah'
     *    }
     *  };
     *  const isUndefined = adventurer.cat?.someNonExistentMethod?.();
     *  const isAlsoUndefined = Utils.safelyGet(adventurer, 'cat', 'someNonExistentMethod');
     *
     * </pre>
     * @param {Object} target
     * @param {string[]} properties
     * @returns {*}
     */
    static safelyCall(target, ...properties) {
      // pass back context of the property so we can have correct bindings of the function
      const [context, fn] = properties.reduce(
        ([currContext, currentTarget], // eslint-disable-line no-unused-vars
          propertyName) => [currentTarget, currentTarget && currentTarget[propertyName]],
        [null, target],
      ) || [];
      return fn && fn.apply(context);
    }

    /**
     * Trace options correspond to vbInitConfig.TRACE_CONFIG. If vbInitConfig.TRACE_CONFIG.tracerOptions.applicationId
     * is not specified, vbInitConfig.APP_ID will be used instead.
     * @returns {Object|undefined} trace options extracted from vbInitConfig
     */
    static getTraceOptions(vbInitConfig) {
      if (vbInitConfig && vbInitConfig.TRACE_CONFIG) {
        const tracerOptions = Object.assign({}, vbInitConfig.TRACE_CONFIG.tracerOptions);
        tracerOptions.applicationId = tracerOptions.applicationId || vbInitConfig.APP_ID;
        return Object.assign({}, vbInitConfig.TRACE_CONFIG, { tracerOptions });
      }
      return undefined;
    }

    /**
     * @returns {boolean} a boolean indicating whether the browser is online.
     */
    static isOnline() {
      // if navigator.onLine is false, it is accurate
      if (!navigator.onLine) {
        return false;
      }

      // Navigator.connection is an experimental feature that is not supported in Safari as of 16.6
      // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection#browser_compatibility
      // @ts-expect-error navigator.connection may not exist, so we need to check for it before accessing its properties
      return !(navigator.connection && navigator.connection.type === 'none');
    }

    /**
     * Add a slash to a path if the path is not empty
     * @param {String} path
     * @return {String}
     */
    static addTrailingSlash(path) {
      let newPath = path || '';
      if (newPath && newPath[newPath.length - 1] !== Constants.PATH_SEPARATOR) {
        newPath = `${newPath}/`;
      }

      return newPath;
    }

    /**
     * If present removes a slash at the end of the path
     * @param {String} path
     * @return {String} path without trailing slash
     */
    static removeTrailingSlash(path) {
      return path && path.slice(-1) === '/' ? path.slice(0, -1) : path;
    }

    /**
     * Return a trimmed version of a string or an empty string if the value is not a string
     *
     * @param  {String}  value  The string to clean
     * @return {String}
     */
    static cleanString(value) {
      return (typeof value === 'string' && value.trim()) || '';
    }

    /**
     * Create new Request object for fetching the token relay endpoint.
     *
     * @param {string} tokenRelayUrl URL of the token relay endpoint.
     * @param {boolean} [isTokenRelay2=false] true if the token relay endpoint is new Trap 2.0.
     * @param {boolean} [addNoCacheHeader=false] if true, add a Cache-Control:no-cache header to the TRAP 2.0 request
     * @returns {Request}
     */
    static createTokenRelayRequest(tokenRelayUrl, isTokenRelay2, addNoCacheHeader = false) {
      const headers = /** @type {Object<string, string|boolean>} */ ({
        [Constants.Headers.USE_OAUTH_ACCESS_TOKEN_WHEN_AVAILABLE]: true,
      });
      const options = {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-cache', // instruct token relay to fetch a fresh token
        headers,
      };

      if (isTokenRelay2) {
        // new TRAP endpoint expects this grant_type
        const body = {
          grant_type: 'urn:oracle:implicit',
        };
        options.body = JSON.stringify(body);
        options.headers['Content-Type'] = 'application/json';

        if (addNoCacheHeader) {
          options.headers['Cache-Control'] = 'no-cache';
        }
      }

      return new Request(tokenRelayUrl, /** @type {RequestInit} */ (options));
    }

    /**
     * Retrieve the default exported module
     *
     * For AMD, the module return  a class or an object directly.
     * For typescript compiled to AMD, the module has a default export.
     * An object with the class in the property name default.
     * @param {*} module
     * @returns {*}
     */
    static getDefaultModule(module) {
      if (Utils.isObject(module) && module.default) {
        return module.default;
      }

      return module;
    }

    /**
     * Function that format log arguments.
     *
     * @param  {...any} logArgs - The log arguments to format.
     * @returns {{ message: Array }} - The formatted log output object.
     */
    static formatArguments(...logArgs) {
      // Result array that will contains the formatted output
      const result = [];

      logArgs.forEach((arg) => {
        if (typeof arg === 'string') {
          result.push(arg);
          return;
        }

        if (arg instanceof Error) {
          const errorObj = {
            'error.message': arg.message,
            'error.stack': arg.stack,
          };
          if ('code' in arg) {
            errorObj['error.code'] = arg.code;
          }
          result.push(errorObj);
          return;
        }

        if (arg && typeof arg === 'object') {
          result.push(arg);
          return;
        }

        result.push(String(arg));
      });

      return { message: result };
    }
  }
  return Utils;
});

